import {
  createBackOfficeTranslator,
  DEFAULT_LOCALE,
  isLocale,
  pickLocale,
  type BackOfficeTranslate,
  type Locale,
} from '@eklevo/shared/i18n/backoffice'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

/*
 * The back office's language (es, ca, en) over its own catalog (ADR 0008): chosen on the page and
 * remembered in this browser only, since the staff have no profile here. The tab's title follows.
 */

const STORAGE_KEY = 'eklevo.locale'

interface I18nValue {
  locale: Locale
  t: BackOfficeTranslate
  setLocale: (locale: Locale) => void
}

const I18nContext = createContext<I18nValue | null>(null)

function initialLocale(): Locale {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (isLocale(stored)) return stored
  } catch {
    // Storage can be unavailable (private mode); fall back to the browser language.
  }
  return pickLocale(navigator.languages ?? [], DEFAULT_LOCALE)
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale)
  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next)
    try {
      window.localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // Not critical: the next visit starts in the browser's language.
    }
  }, [])
  const value = useMemo(
    () => ({ locale, t: createBackOfficeTranslator(locale), setLocale }),
    [locale, setLocale],
  )
  useEffect(() => {
    document.documentElement.lang = locale
    document.title = value.t('app.title')
  }, [locale, value])
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext)
  if (!value) throw new Error('useI18n must be used inside I18nProvider')
  return value
}
